import {assert} from 'Asserts';
import {ProcedureType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {ProcedureFlowchart} from 'procedures/ProcedureDomainTypes';
import {PersistenceInfo} from 'procedures/ProcedurePageContext';

import {processTimingEvent} from './BenchlingProfiler';
import {toDatadogTagFormat} from './PerformanceUtils';
import {ProfilerEvent, ProfilerEventDomain, EventMetadata, NOT_SPECIFIED} from './ProfilerConstants';

/* Should bump version anytime there's a change to logging events or processing them,
 * so that on Datadog, we can quickly see which custom actions have outdated logic or tags
 */
const PROFILER_PROCEDURES_EVENTS_VERSION = 7;

export enum ProfilerProceduresFeatures {
  PROCEDURE_SAVE = 'procedureSave',
  PROCEDURE_CREATE = 'procedureCreate',
  PROCEDURE_MOVE = 'procedureMove',
  PROCEDURE_ARCHIVE_UNARCHIVE = 'procedureArchiveUnarchive',
  PROCEDURE_DUPLICATE = 'procedureDuplicate',
  CREATE_LIBRARY_PARAMETER_DEFINITION = 'createLibraryParameterDefinition',
}
const FEATURE_TO_PROCESSING_FUNCTION: {[key in ProfilerProceduresFeatures]: Function} = {
  [ProfilerProceduresFeatures.PROCEDURE_SAVE]: processProcedureSave,
  [ProfilerProceduresFeatures.PROCEDURE_CREATE]: processProcedureCreate,
  [ProfilerProceduresFeatures.PROCEDURE_MOVE]: processProcedureMove,
  [ProfilerProceduresFeatures.PROCEDURE_ARCHIVE_UNARCHIVE]: processProcedureArchiveUnarchive,
  [ProfilerProceduresFeatures.PROCEDURE_DUPLICATE]: processProcedureDuplicate,
  [ProfilerProceduresFeatures.CREATE_LIBRARY_PARAMETER_DEFINITION]: processCreateLibraryParameterDefinition,
};
enum ProfilerProcedureActions {
  PROCEDURE_SAVE_STARTED = 'procedureSaveStarted',
  PROCEDURE_SAVE_ENDED = 'procedureSaveEnded',
  PROCEDURE_CREATE_STARTED = 'procedureCreateStarted',
  PROCEDURE_CREATE_ENDED = 'procedureCreateEnded',
  PROCEDURE_MOVE_STARTED = 'procedureMoveStarted',
  PROCEDURE_MOVE_ENDED = 'procedureMoveEnded',
  PROCEDURE_ARCHIVE_UNARCHIVE_STARTED = 'procedureArchiveUnarchiveStarted',
  PROCEDURE_ARCHIVE_UNARCHIVE_ENDED = 'procedureArchiveUnarchiveEnded',
  PROCEDURE_DUPLICATE_STARTED = 'procedureDuplicateStarted',
  PROCEDURE_DUPLICATE_ENDED = 'procedureDuplicateEnded',
  CREATE_LIBRARY_PARAMETER_DEFINITION_STARTED = 'createLibraryParameterDefinitionStarted',
  CREATE_LIBRARY_PARAMETER_DEFINITION_ENDED = 'createLibraryParameterDefinitionEnded',
}

export const PROCEDURE_SAVE_STARTED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.PROCEDURES,
  feature: ProfilerProceduresFeatures.PROCEDURE_SAVE,
  action: ProfilerProcedureActions.PROCEDURE_SAVE_STARTED,
};
export const PROCEDURE_SAVE_ENDED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.PROCEDURES,
  feature: ProfilerProceduresFeatures.PROCEDURE_SAVE,
  action: ProfilerProcedureActions.PROCEDURE_SAVE_ENDED,
};

export const PROCEDURE_CREATE_STARTED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.PROCEDURES,
  feature: ProfilerProceduresFeatures.PROCEDURE_CREATE,
  action: ProfilerProcedureActions.PROCEDURE_CREATE_STARTED,
};
export const PROCEDURE_CREATE_ENDED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.PROCEDURES,
  feature: ProfilerProceduresFeatures.PROCEDURE_CREATE,
  action: ProfilerProcedureActions.PROCEDURE_CREATE_ENDED,
};

export const PROCEDURE_MOVE_STARTED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.PROCEDURES,
  feature: ProfilerProceduresFeatures.PROCEDURE_MOVE,
  action: ProfilerProcedureActions.PROCEDURE_MOVE_STARTED,
};
export const PROCEDURE_MOVE_ENDED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.PROCEDURES,
  feature: ProfilerProceduresFeatures.PROCEDURE_MOVE,
  action: ProfilerProcedureActions.PROCEDURE_MOVE_ENDED,
};

export const PROCEDURE_ARCHIVE_UNARCHIVE_STARTED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.PROCEDURES,
  feature: ProfilerProceduresFeatures.PROCEDURE_ARCHIVE_UNARCHIVE,
  action: ProfilerProcedureActions.PROCEDURE_ARCHIVE_UNARCHIVE_STARTED,
};
export const PROCEDURE_ARCHIVE_UNARCHIVE_ENDED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.PROCEDURES,
  feature: ProfilerProceduresFeatures.PROCEDURE_ARCHIVE_UNARCHIVE,
  action: ProfilerProcedureActions.PROCEDURE_ARCHIVE_UNARCHIVE_ENDED,
};

export const PROCEDURE_DUPLICATE_STARTED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.PROCEDURES,
  feature: ProfilerProceduresFeatures.PROCEDURE_DUPLICATE,
  action: ProfilerProcedureActions.PROCEDURE_DUPLICATE_STARTED,
};
export const PROCEDURE_DUPLICATE_ENDED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.PROCEDURES,
  feature: ProfilerProceduresFeatures.PROCEDURE_DUPLICATE,
  action: ProfilerProcedureActions.PROCEDURE_DUPLICATE_ENDED,
};

export const CREATE_LIBRARY_PARAMETER_DEFINITION_STARTED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.PROCEDURES,
  feature: ProfilerProceduresFeatures.CREATE_LIBRARY_PARAMETER_DEFINITION,
  action: ProfilerProcedureActions.CREATE_LIBRARY_PARAMETER_DEFINITION_STARTED,
};
export const CREATE_LIBRARY_PARAMETER_DEFINITION_ENDED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.PROCEDURES,
  feature: ProfilerProceduresFeatures.CREATE_LIBRARY_PARAMETER_DEFINITION,
  action: ProfilerProcedureActions.CREATE_LIBRARY_PARAMETER_DEFINITION_ENDED,
};

export function composeProcedureTypeMetadata({
  procedureType,
  isPow,
}: {
  procedureType: ProcedureType;
  isPow: boolean;
}): EventMetadata {
  // NOTE: All tag keys and values MUST be snake case, alphanumeric and lowercase with no special characters
  // other than hyphens and underscores. This is to match Datadog's tag format expectations.
  return {
    distributionMetadata: {procedure_type: toDatadogTagFormat(procedureType), is_pow: isPow},
    actionMetadata: {procedure_type: toDatadogTagFormat(procedureType), is_pow: isPow},
  };
}

export function composeProcedureEventMetadata(
  isBlocking: boolean,
  procedureType: ProcedureType,
  sizeTrackingMetadata: {
    numSteps: number;
    numNodes: number;
    numParameters: number;
  },
  isPow: boolean,
  blockingSavePersistenceInfo?: PersistenceInfo
): EventMetadata {
  // blockingSavePeristenceInfo is only defined when the save is blocking, otherwise assume autosave
  const objectSource = blockingSavePersistenceInfo?.blockingSaveObjectSource ?? NOT_SPECIFIED;
  const operation = blockingSavePersistenceInfo?.blockingSaveOperation ?? NOT_SPECIFIED;

  // NOTE: All tag keys and values MUST be snake case, alphanumeric and lowercase with no special characters
  // other than hyphens and underscores. This is to match Datadog's tag format expectations.
  return {
    distributionMetadata: {
      is_blocking: isBlocking,
      procedure_type: toDatadogTagFormat(procedureType),
      object_source: toDatadogTagFormat(objectSource),
      operation: toDatadogTagFormat(operation),
      is_pow: isPow,
    },
    actionMetadata: {
      is_blocking: isBlocking,
      procedure_type: toDatadogTagFormat(procedureType),
      object_source: toDatadogTagFormat(objectSource),
      operation: toDatadogTagFormat(operation),
      is_pow: isPow,
      num_methods: sizeTrackingMetadata.numNodes,
      num_steps: sizeTrackingMetadata.numSteps,
      num_parameters: sizeTrackingMetadata.numParameters,
    },
  };
}

export function composeProcedureEventMetadataFromFlowchart(
  isBlocking: boolean,
  procedureType: ProcedureType,
  flowchart: ProcedureFlowchart,
  isPow: boolean,
  blockingSavePersistenceInfo?: PersistenceInfo
): EventMetadata {
  const numSteps = flowchart.nodes.flatMap((node) =>
    node.stepGroups.flatMap((stepGroup) => stepGroup.steps)
  ).length;
  const numParameters = flowchart.nodes.reduce(
    (acc, node) =>
      acc +
      node.dataParameterSettings.length +
      node.materialInputSettings.length +
      node.materialOutputSettings.length +
      node.equipmentSettings.length,
    0
  );
  return composeProcedureEventMetadata(
    isBlocking,
    procedureType,
    {
      numSteps,
      numNodes: flowchart.nodes.length,
      numParameters,
    },
    isPow,
    blockingSavePersistenceInfo
  );
}

/**
 * Build metadata for procedure archive/unarchive events
 * @param isArchiving true when archiving event, false when unarchiving event
 * @returns dictionary with isArchiving key
 */
export function composeProcedureArchiveUnarchiveEventMetadata(
  isArchiving: boolean,
  procedureType: ProcedureType,
  isPow: boolean
): EventMetadata {
  const archiveTypeValue = isArchiving ? 'archive' : 'unarchive';
  // NOTE: All tag keys and values MUST be snake case, alphanumeric and lowercase with no special characters
  // other than hyphens and underscores. This is to match Datadog's tag format expectations.
  return {
    distributionMetadata: {
      archive_type: archiveTypeValue,
      procedure_type: toDatadogTagFormat(procedureType),
      is_pow: isPow,
    },
    actionMetadata: {
      archive_type: archiveTypeValue,
      procedure_type: toDatadogTagFormat(procedureType),
      is_pow: isPow,
    },
  };
}

export function processProceduresEvents(profilerEvent: Pick<ProfilerEvent, 'feature' | 'action'>): void {
  const feature = profilerEvent.feature as ProfilerProceduresFeatures;
  assert(
    Object.values(ProfilerProceduresFeatures).includes(feature),
    `Unexpected profiler feature: ${profilerEvent.feature}`
  );
  FEATURE_TO_PROCESSING_FUNCTION[feature]();
}

/**
 * Process a procedure Save event
 */
function processProcedureSave(): void {
  processTimingEvent(
    PROCEDURE_SAVE_STARTED_EVENT,
    PROCEDURE_SAVE_ENDED_EVENT,
    'Procedure Saved',
    'perf.procedure_saved',
    PROFILER_PROCEDURES_EVENTS_VERSION
  );
}

/**
 * Process a procedure Create event
 */
function processProcedureCreate(): void {
  processTimingEvent(
    PROCEDURE_CREATE_STARTED_EVENT,
    PROCEDURE_CREATE_ENDED_EVENT,
    'Procedure Created',
    'perf.procedure_created',
    PROFILER_PROCEDURES_EVENTS_VERSION
  );
}

/**
 * Process a procedure Move event
 */
function processProcedureMove(): void {
  processTimingEvent(
    PROCEDURE_MOVE_STARTED_EVENT,
    PROCEDURE_MOVE_ENDED_EVENT,
    'Procedure Moved',
    'perf.procedure_moved',
    PROFILER_PROCEDURES_EVENTS_VERSION
  );
}

/**
 * Process a procedure Archive/Unarchive event
 */
function processProcedureArchiveUnarchive(): void {
  processTimingEvent(
    PROCEDURE_ARCHIVE_UNARCHIVE_STARTED_EVENT,
    PROCEDURE_ARCHIVE_UNARCHIVE_ENDED_EVENT,
    'Procedure Archived/Unarchived',
    'perf.procedure_archived_unarchived',
    PROFILER_PROCEDURES_EVENTS_VERSION
  );
}

/**
 * Process a procedure Duplicate event
 */
function processProcedureDuplicate(): void {
  processTimingEvent(
    PROCEDURE_DUPLICATE_STARTED_EVENT,
    PROCEDURE_DUPLICATE_ENDED_EVENT,
    'Procedure Duplicated',
    'perf.procedure_duplicated',
    PROFILER_PROCEDURES_EVENTS_VERSION
  );
}

/**
 * Process creating a library parameter definition.
 */
function processCreateLibraryParameterDefinition(): void {
  processTimingEvent(
    CREATE_LIBRARY_PARAMETER_DEFINITION_STARTED_EVENT,
    CREATE_LIBRARY_PARAMETER_DEFINITION_ENDED_EVENT,
    'Library Parameter Definition Created',
    'perf.library_parameter_definition_created',
    PROFILER_PROCEDURES_EVENTS_VERSION
  );
}
